import React, { useEffect, useState } from 'react';
import { motion, useMotionValue, useTransform, AnimatePresence } from 'framer-motion';
import { ChevronRightIcon, CheckIcon, LockIcon, LoaderIcon } from 'lucide-react';
import { ScrollReveal } from './ScrollReveal';
import { SuccessModal } from './SuccessModal';
import { useClient } from './ClientContext';
const WEBHOOK_URL = 'https://sprooce.app.n8n.cloud/webhook/8cc3f0cd-9dcf-435d-8152-75873e4c8dab';
export function SignatureSection() {
  const [agreed, setAgreed] = useState(false);
  const [approved, setApproved] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [selectedServices, setSelectedServices] = useState<string[]>(['Website & Brand Development']);
  const {
    clientName,
    setClientName,
    companyName,
    setCompanyName
  } = useClient();
  const [formData, setFormData] = useState({
    name: clientName,
    email: '',
    role: '',
    company: companyName,
    date: new Date().toLocaleDateString('en-GB')
  });
  useEffect(() => {
    setClientName(formData.name);
  }, [formData.name, setClientName]);
  useEffect(() => {
    setCompanyName(formData.company);
  }, [formData.company, setCompanyName]);
  const toggleService = (service: string) => {
    setSelectedServices(prev => prev.includes(service) ? prev.filter(s => s !== service) : [...prev, service]);
  };
  const canApprove = agreed && selectedServices.length > 0 && formData.name.trim() !== '' && formData.email.trim() !== '' && formData.role.trim() !== '' && formData.company.trim() !== '';
  const x = useMotionValue(0);
  const xInput = [0, 220];
  const backgroundOpacity = useTransform(x, xInput, [0, 1]);
  const handleDragEnd = () => {
    if (!canApprove) return;
    if (x.get() > 180) {
      setApproved(true);
    } else {
      setApproved(false);
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!approved || !agreed || !canApprove || isSubmitting) return;
    setIsSubmitting(true);
    setSubmitError(null);
    const payload = {
      clientName: formData.name,
      clientEmail: formData.email,
      role: formData.role,
      company: formData.company,
      date: formData.date,
      agreedToTerms: true,
      selectedServices,
      projectName: `Website & Brand Development — ${formData.company}`
    };
    try {
      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        throw new Error(`Submission failed (${response.status}). Please try again.`);
      }
      setShowModal(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section id="sign" className="py-20 md:py-28 px-4 md:px-6 bg-surface border-b border-white/[0.06]" data-id="element-200">
      <SuccessModal isOpen={showModal} onClose={() => setShowModal(false)} data-id="element-201" />

      <div className="max-w-7xl mx-auto" data-id="element-202">
        <ScrollReveal data-id="element-203">
          <div className="mb-12 md:mb-16" data-id="element-204">
            <span className="text-accent font-bold tracking-[0.2em] text-xs md:text-sm uppercase block mb-4" data-id="element-205">
              Agreement
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-white uppercase tracking-tight mb-6 md:mb-8" data-id="element-206">
              Let's create
              <br data-id="element-207" />
              something great.
            </h2>
            <div className="max-w-3xl" data-id="element-208">
              <p className="text-zinc-400 text-base md:text-lg leading-relaxed font-light" data-id="element-209">
                If you're happy to move forward, please approve the proposal
                below. This enables us to secure resources and begin onboarding.
                A deposit invoice will follow, and work will commence once
                payment is received.
              </p>
            </div>
          </div>
        </ScrollReveal>

        <ScrollReveal direction="up" delay={0.2} data-id="element-210">
          <form onSubmit={handleSubmit} className="bg-surface-elevated p-6 md:p-12 lg:p-16 border border-white/[0.06]" data-id="element-211">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-10 md:mb-12" data-id="element-212">
              <div data-id="element-213">
                <label className="block text-[11px] font-bold text-zinc-500 mb-2 uppercase tracking-widest" data-id="element-214">
                  Client name
                </label>
                <input type="text" value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} className="w-full p-3.5 md:p-4 bg-surface border border-white/[0.1] text-white font-mono text-sm md:text-base focus:outline-none focus:border-accent transition-all placeholder:text-zinc-700" data-id="element-215" />
              </div>
              <div data-id="element-216">
                <label className="block text-[11px] font-bold text-zinc-500 mb-2 uppercase tracking-widest" data-id="element-217">
                  Email
                </label>
                <input type="email" placeholder="e.g. name@company.com" required value={formData.email} onChange={e => setFormData({
                ...formData,
                email: e.target.value
              })} className="w-full p-3.5 md:p-4 bg-surface border border-white/[0.1] text-white font-mono text-sm md:text-base focus:outline-none focus:border-accent transition-all placeholder:text-zinc-700" data-id="element-218" />
              </div>
              <div data-id="element-219">
                <label className="block text-[11px] font-bold text-zinc-500 mb-2 uppercase tracking-widest" data-id="element-220">
                  Role
                </label>
                <input type="text" placeholder="e.g. Founder" required value={formData.role} onChange={e => setFormData({
                ...formData,
                role: e.target.value
              })} className="w-full p-3.5 md:p-4 bg-surface border border-white/[0.1] text-white font-mono text-sm md:text-base focus:outline-none focus:border-accent transition-all placeholder:text-zinc-700" data-id="element-221" />
              </div>
              <div data-id="element-222">
                <label className="block text-[11px] font-bold text-zinc-500 mb-2 uppercase tracking-widest" data-id="element-223">
                  Company
                </label>
                <input type="text" value={formData.company} onChange={e => setFormData({
                ...formData,
                company: e.target.value
              })} className="w-full p-3.5 md:p-4 bg-surface border border-white/[0.1] text-white font-mono text-sm md:text-base focus:outline-none focus:border-accent transition-all placeholder:text-zinc-700" data-id="element-224" />
              </div>
              <div data-id="element-225">
                <label className="block text-[11px] font-bold text-zinc-500 mb-2 uppercase tracking-widest" data-id="element-226">
                  Date
                </label>
                <input type="text" value={formData.date} readOnly className="w-full p-3.5 md:p-4 bg-surface border border-white/[0.1] text-zinc-500 font-mono text-sm md:text-base cursor-not-allowed" data-id="element-227" />
              </div>
            </div>

            {/* Service Selection */}
            <div className="mb-10 md:mb-12" data-id="element-228">
              <label className="block text-[11px] font-bold text-zinc-500 mb-4 uppercase tracking-widest" data-id="element-229">
                Services of interest
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-230">
                {[{
                id: 'Website & Brand Development',
                label: 'Website & Brand Development',
                price: '£10,000 + VAT'
              }, {
                id: 'Website Maintenance',
                label: 'Website Maintenance',
                price: '£350/mo + VAT'
              }, {
                id: 'Ongoing SEO & Optimisation',
                label: 'Ongoing SEO & Optimisation',
                price: '£200/mo + VAT'
              }].map(service => {
                const isSelected = selectedServices.includes(service.id);
                return <label key={service.id} className={`flex items-center cursor-pointer group p-4 border transition-colors ${isSelected ? 'border-accent/40 bg-accent/5' : 'border-white/[0.1] hover:bg-white/[0.02]'}`} data-id="element-231">
                      <div className={`w-4 h-4 flex items-center justify-center transition-colors mr-3 flex-shrink-0 border ${isSelected ? 'bg-accent border-accent' : 'border-zinc-600 group-hover:border-accent'}`} data-id="element-232">
                        {isSelected && <CheckIcon className="w-3 h-3 text-white" data-id="element-233" />}
                      </div>
                      <input type="checkbox" checked={isSelected} onChange={() => toggleService(service.id)} className="hidden" data-id="element-234" />
                      <div className="flex-1 min-w-0" data-id="element-235">
                        <span className="text-xs md:text-sm text-white font-bold uppercase tracking-wide block" data-id="element-236">
                          {service.label}
                        </span>
                        <span className="text-[10px] text-zinc-500 font-mono" data-id="element-237">
                          {service.price}
                        </span>
                      </div>
                    </label>;
              })}
              </div>
              {selectedServices.length === 0 && <p className="text-[10px] text-amber-500/80 mt-2 uppercase tracking-wide" data-id="element-238">
                  Please select at least one service
                </p>}
            </div>

            <div className="mb-10 md:mb-12" data-id="element-239">
              <label className="flex items-center cursor-pointer group p-4 md:p-5 border border-white/[0.1] hover:bg-white/[0.02] transition-colors" data-id="element-240">
                <div className={`w-5 h-5 flex items-center justify-center transition-colors mr-4 flex-shrink-0 border ${agreed ? 'bg-accent border-accent' : 'border-zinc-600 group-hover:border-accent'}`} data-id="element-241">
                  {agreed && <CheckIcon className="w-3.5 h-3.5 text-white" data-id="element-242" />}
                </div>
                <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} className="hidden" data-id="element-243" />
                <span className="text-xs md:text-sm text-zinc-400 font-mono uppercase tracking-wide" data-id="element-244">
                  I agree to the terms and conditions outlined above
                </span>
              </label>
            </div>

            {/* Slide to Approve */}
            <div className="mb-8" data-id="element-245">
              <label className="block text-[11px] font-bold text-zinc-500 mb-3 uppercase tracking-widest" data-id="element-246">
                Approve
              </label>
              <div className={`relative w-[280px] h-[60px] rounded-full overflow-hidden flex items-center transition-opacity duration-300 ${canApprove ? 'bg-black border border-white/[0.1]' : 'bg-zinc-900 cursor-not-allowed border border-white/[0.05]'}`} data-id="element-247">
                <motion.div className="absolute inset-0 bg-emerald-500" style={{
                opacity: approved ? 1 : canApprove ? backgroundOpacity : 0
              }} data-id="element-248" />

                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10" data-id="element-249">
                  {!canApprove && !approved ? <span className="text-white/40 font-bold tracking-widest text-xs uppercase flex items-center gap-2" data-id="element-250">
                      <LockIcon className="w-3 h-3" data-id="element-251" />
                      Complete fields
                    </span> : <span className="text-white font-bold tracking-widest text-xs uppercase" data-id="element-252">
                      {approved ? 'Approved' : 'Slide to approve'}
                    </span>}
                </div>

                {canApprove && <motion.div className="w-[52px] h-[52px] bg-white rounded-full absolute left-1 flex items-center justify-center z-20 cursor-grab active:cursor-grabbing" drag="x" dragConstraints={{
                left: 0,
                right: 220
              }} dragElastic={0.1} dragSnapToOrigin={!approved} onDragEnd={handleDragEnd} style={{
                x: approved ? 220 : x
              }} data-id="element-253">
                    {approved ? <CheckIcon className="w-5 h-5 text-emerald-500" data-id="element-254" /> : <ChevronRightIcon className="w-5 h-5 text-black" data-id="element-255" />}
                  </motion.div>}
              </div>
              {!canApprove && <p className="text-[11px] text-zinc-600 mt-3 max-w-[280px] uppercase tracking-wide" data-id="element-256">
                  Complete all fields to approve
                </p>}
            </div>

            {/* Submit button */}
            <div className={`transition-all duration-500 overflow-hidden ${approved && agreed && formData.role ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-257">
              <button type="submit" disabled={isSubmitting} className={`w-full py-4 md:py-5 font-bold uppercase tracking-widest text-sm md:text-base transition-all flex items-center justify-center gap-2 ${isSubmitting ? 'bg-zinc-800 text-white cursor-not-allowed' : 'bg-accent text-white hover:bg-accent-hover'}`} data-id="element-258">
                {isSubmitting ? <>
                    <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-259" />
                    Submitting...
                  </> : 'Submit proposal'}
              </button>

              <AnimatePresence data-id="element-260">
                {submitError && <motion.div initial={{
                opacity: 0,
                y: -8
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -8
              }} className="mt-4 p-4 bg-red-500/10 border border-red-500/20 text-red-400 text-xs md:text-sm font-mono text-center" data-id="element-261">
                    {submitError}
                  </motion.div>}
              </AnimatePresence>
            </div>
          </form>
        </ScrollReveal>
      </div>
    </section>;
}